import { FC } from 'react'

import { ChevronPrevIcon, ChevronNextIcon, ExitIcon } from '@svgs'
import { Navigation } from './components'
import LanguageSegments from 'src/components/elements/language-segments/LanguageSegments'
import ThemeSegments from 'src/components/elements/theme-segments/ThemeSegments'

import logo from '@images/logo.png'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { api, useLogoutMutation } from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { useSidebar } from 'src/contexts/SidebarContext'
import clsx from 'clsx'

interface ISidebar {}

const Sidebar: FC<ISidebar> = () => {
  const [logout] = useLogoutMutation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { collapsed, toggleSidebar } = useSidebar()

  const handleLogout = async () => {
    try {
      await logout(undefined).unwrap()
      dispatch(api.util.resetApiState())
      navigate('/login')
    } catch {
      // error toast is shown by the global toast middleware
    }
  }

  const { t } = useTranslation()
  return (
    <aside
      className={clsx(
        'flex flex-col gap-10 pt-7 shrink-0 bg-surface min-h-screen transition-all duration-300',
        collapsed ? 'w-[5rem]' : 'w-[23rem]',
      )}
    >
      <div className="flex flex-col gap-8">
        <div className={clsx('flex flex-col gap-7', collapsed ? 'px-3' : 'px-8')}>
          <div
            className={clsx(
              'flex items-center',
              collapsed ? 'flex-col gap-4 justify-center' : 'gap-5 justify-between',
            )}
          >
            {!collapsed && (
              <Link
                to={'/'}
                data-logo
                className="flex items-center gap-3 text-2xl font-black leading-7 text-ink"
              >
                <div className="w-16 flex-shrink-0">
                  <img
                    loading="lazy"
                    src={logo}
                    alt="Private Agreement"
                    className="object-contain size-full"
                  />
                </div>
                <div className="flex flex-col">
                  Private <br />
                  <span className="text-ink-2"> Agreement</span>
                </div>
              </Link>
            )}
            {collapsed && (
              <Link to={'/'} className="flex items-center">
                <div className="w-11">
                  <img
                    loading="lazy"
                    src={logo}
                    alt="Private Agreement"
                    className="object-contain size-full"
                  />
                </div>
              </Link>
            )}
            <button
              onClick={toggleSidebar}
              className="icon w-11 h-11 bg-page rounded-xl fill-ink p-[.9375rem] hover:bg-brand-soft transition-colors"
              aria-label={collapsed ? t('expand_sidebar') : t('collapse_sidebar')}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronNextIcon /> : <ChevronPrevIcon />}
            </button>
          </div>
        </div>
        <div className="flex flex-col">
          <Navigation collapsed={collapsed} />
          {!collapsed && (
            <div className="flex flex-col gap-2 px-8 pt-5">
              <p className="m-0 text-xs font-extrabold uppercase tracking-[0.04em] text-ink-2">
                {t('language')}
              </p>
              <LanguageSegments />
              <ThemeSegments
                className="mt-3"
                legendClassName="!mb-2 !text-xs !font-extrabold !uppercase !tracking-[0.04em] !text-ink-2 !leading-4"
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-auto flex flex-col">
        <div className="w-full h-px bg-ink/[0.08]" />
        {/* the danger colour of the system: red-500 read at 3.8:1 */}
        <button
          onClick={handleLogout}
          className={clsx(
            'flex gap-4 items-center min-h-14 text-base font-bold text-danger fill-danger w-full hover:bg-danger-soft transition-colors',
            collapsed ? 'px-3 justify-center' : 'px-8',
          )}
          title={collapsed ? t('log_out') : undefined}
          aria-label={collapsed ? t('log_out') : undefined}
        >
          <span className="icon-button w-4 h-4">
            <ExitIcon />
          </span>
          {!collapsed && t('log_out')}
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
